import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AutoFocusTextarea from './ui/AutoFocusTextarea';
import ErrorBoundary from './ErrorBoundary';
import Button from './ui/button';
import MoodSlider from './MoodSlider';
import BreathingOrb from './visualizations/BreathingOrb';
import MorningCheckInComplete from './MorningCheckInComplete';
import { useToast } from '../hooks/useToast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface MorningCheckInProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface MorningQuestions {
  potentialQuestion: string;
  goalQuestion: string;
  energyQuestion: string;
}

// Get personalized morning questions based on user's onboarding priority
const getMorningQuestions = (): MorningQuestions => {
  const onboardingAnswers = localStorage.getItem('onboardingAnswers');
  let priority = 'your growth goal';
  
  if (onboardingAnswers) {
    try {
      const answers = JSON.parse(onboardingAnswers);
      priority = (answers.priority || 'your growth goal').toLowerCase();
    } catch (error) {
      console.error('Error parsing onboarding answers:', error);
    }
  }
  
  return {
    potentialQuestion: `How are you feeling about today's potential?`,
    goalQuestion: `Today, my priority is`,
    energyQuestion: `Rate your current energy and motivation`
  };
};

const MorningCheckIn: React.FC<MorningCheckInProps> = ({ onSuccess, onCancel }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({
    moodRating: 3,
    potentialRating: 5,
    primaryGoal: '',
    energyMotivation: 5
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [momentumDays, setMomentumDays] = useState(1);
  const [startTime] = useState(Date.now());
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  const questions = getMorningQuestions();
  const questionTypes = useMemo(() => ['mood', 'rating', 'text', 'rating'] as const, []);
  const questionLabels = useMemo(() => [
    '', // Empty string for mood question since MoodSlider handles its own display
    questions.potentialQuestion,
    questions.goalQuestion,
    questions.energyQuestion
  ], [questions]);

  // Auto-focus textarea when it becomes visible
  useEffect(() => {
    if (questionTypes[currentQuestionIndex] === 'text' && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [currentQuestionIndex, questionTypes]);

  // Debug: Monitor showCompletion state changes
  useEffect(() => {
    console.log('🎯 showCompletion state changed to:', showCompletion);
  }, [showCompletion]);

  const handleNext = () => {
    // Validate current response
    if (questionTypes[currentQuestionIndex] === 'text' && !responses.primaryGoal.trim()) {
      showToast('Please provide an answer before continuing', 'error');
      return;
    }

    if (currentQuestionIndex < questionLabels.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleMoodChange = (moodValue: number) => {
    setResponses(prev => ({ ...prev, moodRating: moodValue }));
    handleNext(); // Auto-advance after mood selection
  };

  const handleRatingChange = (rating: number) => {
    if (currentQuestionIndex === 1) {
      setResponses(prev => ({ ...prev, potentialRating: rating }));
    } else if (currentQuestionIndex === 3) {
      setResponses(prev => ({ ...prev, energyMotivation: rating }));
    }
  };

  const handleTextChange = (text: string) => {
    setResponses(prev => ({ ...prev, primaryGoal: text }));
  };

  const handleSubmit = async () => {
    console.log('🚀 Starting morning check-in submission...');
    setIsSubmitting(true);
    const responseTime = Math.floor((Date.now() - startTime) / 1000);
    
    try {
      const token = localStorage.getItem('token');
      const accessGranted = localStorage.getItem('thinkmate_access_granted');
      console.log('🔑 Auth check - token:', !!token, 'accessGranted:', accessGranted);
      
      if (!token && accessGranted !== 'true') {
        console.log('❌ Auth failed - redirecting to signup');
        showToast('Please log in to continue', 'error');
        navigate('/signup');
        return;
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['x-auth-token'] = token;
      }
      
      // Add access granted header if password protection was bypassed
      if (accessGranted === 'true') {
        headers['x-access-granted'] = 'true';
      }

      console.log('📡 Making API call with headers:', headers);
      console.log('📤 Request body:', {
        moodRating: responses.moodRating,
        potentialRating: responses.potentialRating,
        primaryGoal: responses.primaryGoal.trim(),
        energyMotivation: responses.energyMotivation,
        responseTime
      });

      const response = await fetch(`${API_URL}/api/daily-checkins/morning`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          moodRating: responses.moodRating,
          potentialRating: responses.potentialRating,
          primaryGoal: responses.primaryGoal.trim(),
          energyMotivation: responses.energyMotivation,
          responseTime
        })
      });

      console.log('📡 Response status:', response.status, response.statusText);

      if (!response.ok) {
        console.log('❌ Response not OK, parsing error...');
        const errorData = await response.json();
        console.log('❌ Error data:', errorData);
        throw new Error(errorData.msg || 'Failed to submit morning check-in');
      }

      const data = await response.json();
      console.log('✅ Morning check-in response:', data);
      
      // Get momentum days from response or calculate from existing streak
      if (data.momentumDays) {
        setMomentumDays(data.momentumDays);
        console.log('🔥 Set momentum days from response:', data.momentumDays);
      }
      
      console.log('🎉 About to show completion screen with momentumDays:', data.momentumDays || momentumDays);
      console.log('🎉 Current showCompletion state:', showCompletion);
      
      // Show completion screen instead of immediately navigating
      setShowCompletion(true);
      console.log('🎉 setShowCompletion(true) called!');
      
      // Don't reset isSubmitting here - let completion screen handle it
      setIsSubmitting(false);

    } catch (error) {
      console.error('Morning check-in submission error:', error);
      showToast(
        error instanceof Error ? error.message : 'Failed to submit check-in', 
        'error'
      );
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleNext();
    }
  };

  const handleCompletionFinish = () => {
    if (onSuccess) {
      onSuccess();
    } else {
      navigate('/dashboard');
    }
  };

  // Show completion screen if check-in is completed
  console.log('🔍 Render check - showCompletion:', showCompletion, 'declaration:', responses.primaryGoal);
  if (showCompletion) {
    console.log('✅ Rendering MorningCheckInComplete with:', { declaration: responses.primaryGoal, momentumDays });
    return (
      <MorningCheckInComplete
        declaration={responses.primaryGoal}
        momentumDays={momentumDays}
        onComplete={handleCompletionFinish}
      />
    );
  }

  const currentQuestionType = questionTypes[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questionLabels.length) * 100;
  const isMoodQuestion = currentQuestionType === 'mood';

  return (
    <ErrorBoundary>
      <div className={`min-h-screen flex items-center justify-center p-6 md:p-4 ${
        isMoodQuestion 
          ? 'bg-gradient-to-br from-slate-800 to-slate-900' 
          : 'bg-gradient-to-br from-orange-50 to-yellow-50'
      }`}>
        <div className={`rounded-2xl shadow-xl w-full max-w-md p-8 md:p-6 space-y-6 ${
          isMoodQuestion ? 'bg-transparent' : 'bg-white'
        }`}>
          {/* Header - Hidden for mood question */}
          {!isMoodQuestion && (
            <>
              <div className="text-center">
                <div className="flex items-center justify-center mb-4">
                  <BreathingOrb size={120} momentumLevel={responses.energyMotivation} />
                </div>
                <h1 className="text-2xl font-bold text-text-primary mb-2">Good Morning!</h1>
                <p className="text-text-secondary text-sm">Let's start your day with intention</p>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-orange-500 to-yellow-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-text-secondary text-center">
                Question {currentQuestionIndex + 1} of {questionLabels.length}
              </p>
            </>
          )}

          {/* Question */}
          <div className="space-y-1">
            {/* Mood Slider */}
            {currentQuestionType === 'mood' && (
              <MoodSlider 
                onContinue={handleMoodChange}
                disabled={isSubmitting}
              />
            )}

            {/* Regular Questions */}
            {currentQuestionType !== 'mood' && (
              <>
                <h2 className="text-lg font-semibold text-text-primary text-center">
                  {questionLabels[currentQuestionIndex]}
                </h2>

                {/* Rating Input */}
                {currentQuestionType === 'rating' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center text-sm text-text-secondary px-2">
                  <span>Low</span>
                  <span>High</span>
                </div>
                <div className="flex justify-center space-x-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => {
                    const currentRating = currentQuestionIndex === 0 
                      ? responses.potentialRating 
                      : responses.energyMotivation;
                    
                    return (
                      <button
                        key={rating}
                        onClick={() => handleRatingChange(rating)}
                        className={`w-12 h-12 md:w-10 md:h-10 rounded-full border-2 text-sm font-semibold transition-all duration-200 ${
                          currentRating === rating
                            ? 'bg-gradient-to-r from-orange-500 to-yellow-500 border-orange-500 text-white shadow-lg scale-110'
                            : 'border-gray-300 text-text-secondary hover:border-orange-300 hover:bg-orange-50'
                        }`}
                      >
                        {rating}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Text Input */}
            {currentQuestionType === 'text' && (
              <div className="space-y-2">
                <AutoFocusTextarea
                  ref={textareaRef}
                  value={responses.primaryGoal}
                  onChange={(e) => handleTextChange(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Write your focus..."
                  className="w-full p-5 md:p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-orange-500 focus:border-transparent min-h-[120px] md:min-h-[100px] text-base md:text-sm text-text-primary"
                  maxLength={500}
                />
                <p className="text-xs text-text-secondary text-right">
                  {responses.primaryGoal.length}/500 characters
                </p>
              </div>
            )}
              </>
            )}
          </div>

          {/* Navigation Buttons - Hidden for mood question */}
          {!isMoodQuestion && (
            <div className="flex justify-between items-center pt-4">
            <Button
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              variant="ghost"
              ambient={true}
              className="px-5 py-3 md:px-4 md:py-2 disabled:text-gray-400 disabled:cursor-not-allowed min-h-[48px] md:min-h-auto flex items-center"
            >
              ← Previous
            </Button>

            <Button
              onClick={handleNext}
              disabled={isSubmitting || (currentQuestionType === 'text' && !responses.primaryGoal.trim())}
              ambient={true}
              className="px-6 py-3 md:py-2 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:from-orange-600 hover:to-yellow-600 transition-all duration-200 shadow-lg min-h-[48px] md:min-h-auto flex items-center justify-center"
            >
              {isSubmitting ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Submitting...</span>
                </div>
              ) : currentQuestionIndex === questionLabels.length - 1 ? (
                'Complete Check-in'
              ) : (
                'Next →'
              )}
            </Button>
            </div>
          )}

          {/* Cancel Link */}
          {onCancel && (
            <div className="text-center pt-2">
              <Button
                onClick={onCancel}
                variant="ghost"
                ambient={true}
                className="text-base md:text-sm py-2 min-h-[44px] flex items-center justify-center"
              >
                Cancel and return to dashboard
              </Button>
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default MorningCheckIn;